import type { CarConfigDispatchType, CarConfigStateType } from "../redux/store";
import type { ExternalConfigType } from "../../../shared-logic/types/ExternalPriceType";
import { type CarConfigLabelType, carConfigIntegratedTradeInNotificationsLabels } from "./labels";
import { addNotification } from "../../../../common-deprecated/redux/actions/NotificationActions";
import { setTradeInNotification } from "../../../../common-deprecated/features/tradein/redux/actions/TradeInAemExternalDataActions";
import { getExternalConfigOptionsAccessoryIds } from "../../../shared-logic/utils/typedUtils";
import { extractAdditionalInfoFromExternalPrice } from "../../../shared-logic/utils/externalPriceUtils";
import { getCarConfigLabel } from "./labelUtils";
import { isAllowedFinanceProductForTradeIn } from "./carConfigSettingsUtils";

export const getExternalConfigurationObject = (state: CarConfigStateType): ExternalConfigType => {
    const { commonSettings, car, basket, items, finance, externalPrice } = state;

    const externalConfig: ExternalConfigType = {
        Country: commonSettings.country,
        Brand: commonSettings.brand,
        Language: commonSettings.language,
        ModelID: car.model.id,
        CarID: car.id,
        ExteriorColourID: basket.colour.id || car.colour.id,
        UpholsteryID: basket.upholstery.id || car.upholstery.id,
        Parameters: getParametersForExternalConfigurationObject(state),
    };

    const tmefAdditionalInfo = extractAdditionalInfoFromExternalPrice(externalPrice.calculatedPrice);
    if (tmefAdditionalInfo) {
        if (!externalConfig.Parameters) externalConfig.Parameters = {};
        if (!externalConfig.Parameters.Specifications) externalConfig.Parameters.Specifications = {};
        externalConfig.Parameters.Specifications.AdditionalInfo = tmefAdditionalInfo;
    }

    if (basket.packs.length > 0) externalConfig.Packs = basket.packs.map((pack) => pack.id);
    if (basket.serviceProducts.length > 0)
        externalConfig.ServiceProducts = basket.serviceProducts.map((serviceProduct) => serviceProduct.id);

    const { options, accessories } = getExternalConfigOptionsAccessoryIds(basket, items);

    if (basket.winterWheel && !basket.winterWheel.installTheWheels) {
        externalConfig.ExcludeMountingCosts = [basket.winterWheel.id];
    }

    if (finance.financeProductType) {
        externalConfig.ProductType = finance.financeProductType;
    }

    if (options.length > 0) externalConfig.Options = options;
    if (accessories.length > 0) externalConfig.Accessories = accessories;

    if (basket.total) {
        const { discount, promotions, onlineMonthlyDiscount } = basket.total;

        const ecoBonusPromotionsPrice = promotions
            .filter((promotion) => promotion.ecoBonus)
            .reduce((initialValue, promotion) => initialValue + promotion.amount, 0);

        if (promotions && promotions.length > 0) {
            externalConfig.Promotions = promotions.map((promo) => promo.id);
        }

        // Remove the price of the non financeable serviceProducts from the total price so it won't affect the monthly rates
        // Add the price of the ecobonus promotions as these are not meant to be reducted when sending to the finance service
        externalConfig.TotalPrice = discount + ecoBonusPromotionsPrice;
        // Dynamically add it to the ExternalConfig object, considering as soon as it's defined it will return its value in the ccGetMonthlyPayment
        if (discount !== onlineMonthlyDiscount) {
            externalConfig.TotalPriceInclPromotions = onlineMonthlyDiscount;
        }
    }

    // if (externalPrice.calculatedPrice.extraCosts) {
    //     if (!externalConfig.Costs) externalConfig.Costs = [];
    //     // TODO: what with Value? Needs to be alligned further as there can normally be multiple values
    //     externalConfig.Costs = externalPrice.calculatedPrice.extraCosts.map((cost) => ({
    //         Name: cost.key,
    //         TaxDefCode: typeof cost.eposVatCode !== "undefined" ? String(cost.eposVatCode) : undefined,
    //         Code: cost.eposCode || "",
    //         Amount: cost.values[0]?.amount,
    //         Selected: cost?.eposSelectionIndicator === "YES",
    //     }));
    // }

    return externalConfig;
};

export const getParametersForExternalConfigurationObject = (
    state: CarConfigStateType,
): ExternalConfigType["Parameters"] => {
    const { utils, finance, tradeInAemExternalData, externalPrice } = state;
    const { disableTradeInForFinanceProducts } = state.carConfigSettings;

    const parameters: ExternalConfigType["Parameters"] = { Origin: "apheleia-carconfig" };

    if (finance.quotes && finance.quotes.enabled && (finance.quotes.finance || finance.quotes.insurance)) {
        if (finance.quotes.finance) parameters.FinanceQuoteID = finance.quotes.finance;
        if (finance.quotes.insurance) parameters.InsuranceQuoteID = finance.quotes.insurance;
    }

    if (utils.financeOption) parameters.FinanceOption = utils.financeOption;

    // Only add trade-in value if it's compatible with the current financeProduct.
    if (tradeInAemExternalData.data.vehicleValue) {
        if (isAllowedFinanceProductForTradeIn(disableTradeInForFinanceProducts, finance.info.productName)) {
            parameters.TradeInValue = tradeInAemExternalData.data.vehicleValue;
            parameters.TradeInQuoteID = tradeInAemExternalData.data.quoteId;
        }
    }

    if (externalPrice.co2Specs.value) {
        parameters.Specifications = {
            Co2: Number(externalPrice.co2Specs.value),
        };
    }

    return parameters;
};

export const numberIsEven = (number: number): boolean => number % 2 === 0;

/**
 * Triggers pending notifications for the integrated trade-in based on the finance option
 */
export const showTradeInNotification = (state: CarConfigStateType, dispatch: CarConfigDispatchType): void => {
    const { utils, carConfigSettings, commonSettings, tradeInAemExternalData } = state;

    const tradeInNotification = tradeInAemExternalData.notification[utils.financeOption];

    if (tradeInNotification?.show) {
        const { notificationType, isError } = tradeInNotification;

        let labelKey: CarConfigLabelType | null = null;

        if (notificationType) {
            labelKey = carConfigIntegratedTradeInNotificationsLabels[notificationType];
        } else if (isError) {
            labelKey =
                utils.financeOption === "cash"
                    ? "carConfigTradeinNotificationTooHighCash"
                    : "carConfigTradeinNotificationTooHighMonthly";
        }

        const title = getCarConfigLabel({ carConfigSettings, commonSettings }, "carConfigTradeinNotificationUpdate");
        const description = labelKey ? getCarConfigLabel({ carConfigSettings, commonSettings }, labelKey) : "";

        if (description) {
            // Triggers the real notification
            dispatch(
                addNotification([
                    {
                        title,
                        description,
                        action: { closeButton: true },
                    },
                ]),
            );
            // Set show back to false, so it won't be shown multiple times
            dispatch(setTradeInNotification(utils.financeOption, false));
        }
    }
};
